import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { AUTH_ERROR_CODES, type AuthErrorBody } from '@vevents/auth';

import { SudoPromptService } from '../services/sudo-prompt.service';

/**
 * MOB-100 Stage 18 — mobile sudo interceptor.
 *
 * Symmetrical with the Stage 15B web `sudoInterceptor` but talks to the
 * mobile `SudoPromptService`. Catches `403` responses whose body carries
 * `{ errorCode: 'sudo-required' }`, opens the modal sudo prompt, and on
 * grant replays the original request verbatim.
 *
 * Edge cases (Stage 18 prompt + Stage 15B parity):
 *  - `/api/auth/sudo` itself is excluded so the prompt cannot recurse on
 *    its own challenge response.
 *  - `/api/auth/login` and `/api/auth/refresh` are excluded so a 403
 *    surfaced there can never trigger a sudo prompt.
 *  - When the user cancels the prompt (or the request times out into the
 *    lockout state), we propagate the original 403 to the caller — the
 *    interceptor does NOT swallow it.
 *  - The lockout modal stays visible after a 429 — the caller's 403
 *    propagates so the calling screen can react, but the modal sticks.
 *
 * Ordering: this interceptor MUST run BEFORE `authInterceptor` because
 * the 403 sudo-required body is structured differently from a generic
 * 401 token-expired body. See `app.config.ts`.
 */
export const sudoInterceptor: HttpInterceptorFn = (req, next) => {
  const promptService = inject(SudoPromptService);

  if (isSudoOwnRequest(req) || isAuthBootstrapRequest(req)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!isSudoRequiredError(err)) {
        return throwError(() => err);
      }
      const triggeringAction = inferTriggeringAction(req);
      return from(promptService.requestSudo({ triggeringAction })).pipe(
        switchMap((result) => {
          if (!result.granted) {
            return throwError(() => err);
          }
          return retryRequest(req, next);
        })
      );
    })
  );
};

function retryRequest(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  return next(req);
}

function isSudoOwnRequest(req: HttpRequest<unknown>): boolean {
  const url = req.url || '';
  return url.includes('/api/auth/sudo');
}

function isAuthBootstrapRequest(req: HttpRequest<unknown>): boolean {
  const url = req.url || '';
  return (
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/refresh') ||
    url.includes('/api/auth/resolve-token') ||
    url.includes('/api/auth/device-verification/')
  );
}

function isSudoRequiredError(err: unknown): err is HttpErrorResponse {
  if (!(err instanceof HttpErrorResponse) || err.status !== 403) return false;
  const body = err.error as Partial<AuthErrorBody> | null | undefined;
  if (!body || typeof body !== 'object') return false;
  return body.errorCode === AUTH_ERROR_CODES.SudoRequired;
}

/**
 * Best-effort audit-action label so the server-side audit log captures
 * which mobile intent triggered the prompt. Mobile uses a `mobile:` prefix
 * to distinguish from the web counterpart.
 */
function inferTriggeringAction(req: HttpRequest<unknown>): string {
  const url = req.url || '';
  const method = (req.method || 'GET').toUpperCase();
  if (url.includes('/admin/eventadmin/toggle-status')) return 'mobile:event.publish';
  if (/\/api\/events\/\d+/.test(url) && method === 'DELETE') return 'mobile:event.delete';
  if (url.includes('/reconciliation/refund')) return 'mobile:registration.refund';
  if (url.includes('/booth/leads')) return 'mobile:booth.viewLeads';
  return `mobile:${method.toLowerCase()}:${stripQuery(url)}`;
}

function stripQuery(url: string): string {
  const queryIndex = url.indexOf('?');
  return queryIndex >= 0 ? url.slice(0, queryIndex) : url;
}
